import { useContext, useMemo } from 'react';
import UserContext from '@lib/contexts/userContext';
import { useQuery } from '@tanstack/react-query';
import { axiosRequest } from '@lib/axios';
import moment from '@lib/moment';
import useStaffUserLookup from "@components/common/hooks/useStaffUserLookup";
import { getSessionName, getSessionOrder } from "@components/Pages/Events/Sessions/sessionFunctions";

const useSessionsQuery = (params) => {
  const { authenticated } = useContext(UserContext);
  const { isStaffLoading, isStaffRefetching, isStaffError, staffError, setMetadataNames } = useStaffUserLookup();

  // The filters are stored in an array, not an object, to allow the same field name to appear multiple times
  // in the query string, e.g., created_by=alice@example.com&created_by=bob@example.com
  const filters = useMemo(() => {
    const f = Object.keys(params?.filters).reduce((acc, key) => {
      const value = params?.filters[key];
      if (!value) {
        return acc;
      }
      const valuesArray = Array.isArray(value) ? value : [value];

      switch (key) {
        case 'project':
          // question options all get grouped by event_answers when sent to the backend
          // multiple groups get sent as an array of comma delineated strings
          acc.push(['event.event_answers', valuesArray]);
          break;
        case 'delivery_location_id':
        case 'created_by':
          valuesArray.forEach(v => acc.push([`${key}`, v.id || v]));
          break;
        // Other fields, e.g., status or end date, may be passed in from the parent component,
        // which are not user configurable.
        default:
          acc.push([key, value])
          break;
      }
      return acc;
    }, []);
    return f;
  }, [params.filters]);

  const query = useMemo(() => {
    const q = new URLSearchParams(filters);
    return q;
  }, [filters]);


  const {
    data,
    isLoading,
    isRefetching,
    error,
    isError,
  } = useQuery({
    queryKey: ['sessions', filters],
    queryFn: () => axiosRequest({
      method: 'GET',
      url: `sessions?${query.toString()}`,
    }),
    enabled: authenticated && !isStaffLoading,
    refetchOnWindowFocus: false,
    retry: false,
    select: (sessions) => {
      sessions.forEach(session => {
        session.name = getSessionName({
          session,
          dateFallback: false,
          useNill: true,
        });
        session.mStart = moment(session.start);
        session.mEnd = moment(session.end);
        session.sessionOrder = getSessionOrder(session);
        setMetadataNames(session);
        setMetadataNames(session.event);
      });
      return sessions;
    }
  });

  const loading = (isLoading) || (isStaffLoading);
  const refetching = (isRefetching) || (isStaffRefetching);

  return {
    data,
    loading,
    // refetch,
    refetching,
    error: error || staffError,
    isError: isError || isStaffError,
  }
};

export default useSessionsQuery;
